import React, { useEffect, useRef, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import CompetitionEntryTable from '../../../components/tables/CompetitionEntryTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useCompetitionEntryApi } from '../../../hooks/useCompetitionEntryApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete, confirmAction, showAlert } from '../../../utils/sweetAlertUtils';
import { CompetitionEntryDTO } from '../../../services/apiClient';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSearchState } from '../../../utils/searchStateUtils/useSearchState';
import GeneralSearch from '../../../components/common/GeneralSearch';
import { getRoles } from '../../../utils/tokenUtils';
import { useCompetitionClassApi } from '../../../hooks/useCompetitionClassApi';
import { usePaymentApi } from '../../../hooks/usePaymentApi';
import { CompetitionStatusConstants } from '../../../constants/CompetitionStatusConstants';
import { getAdjustedPageAfterMutation } from '../../../utils/paginationUtils';
import { ChevronDown } from 'lucide-react';

const CompetitionEntryList: React.FC = () => {
  const roles = getRoles();
  const { search, remove, extract, updateStatus, isLoading, error } = useCompetitionEntryApi();
  const { processEftPayment } = usePaymentApi();
  const [items, setItems] = useState<CompetitionEntryDTO[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const bulkActionsRef = useRef<HTMLDivElement>(null);

  const { } = useCompetitionClassApi

  // Search state persistence
  const {
    currentPage,
    pageSize,
    sortBy,
    sortDescending,
    searchFilters,
    setCurrentPage,
    setPageSize,
    setSortBy,
    setSortDescending,
    setSearchFilters,
    clearSearchState,
  } = useSearchState('competitionentries', {
    currentPage: 1,
    pageSize: 10,
    sortBy: 'CreatedUtc',
    sortDescending: true,
    searchFilters: {},
    showAdvanced: false,
  });

  const navigate = useNavigate();
  const location = useLocation();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Competition Entries', href: '#' },
  ];

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      const response = await search({
        filters,
        page,
        pageSize: size,
        sortBy,
        sortDescending,
      });
      setItems(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
      return response?.data?.totalCount || 0;
    } catch (error) {
      console.error('Error fetching Competition Entries:', error);
      return 0;
    }
  };

  const handleSort = (sortKey: string) => {
    if (sortBy === sortKey) {
      setSortDescending(!sortDescending);
    } else {
      setSortBy(sortKey);
      setSortDescending(true);
    }
    setCurrentPage(1);
  };

  /**
   * Refreshes the list after a mutation (confirm, reject, etc.),
   * automatically adjusting to a valid page if the current page is now out of range.
   */
  const refreshAfterMutation = async () => {
    const newTotalCount = await fetchItems(currentPage, pageSize, searchFilters);
    const adjustedPage = getAdjustedPageAfterMutation(currentPage, pageSize, newTotalCount);

    if (adjustedPage !== currentPage) {
      setCurrentPage(adjustedPage);
      await fetchItems(adjustedPage, pageSize, searchFilters);
    }
  };

  const previousFiltersRef = useRef<string>('');

  // Sync check: get current URL filters on every render to detect mismatches
  const getCurrentUrlFilters = () => {
    const params = new URLSearchParams(location.search);
    const reservedParams = ['page', 'pageSize', 'sortBy', 'sortDesc', 'showAdvanced', 'view'];
    const urlFilters: Record<string, string> = {};
    params.forEach((value, key) => {
      if (!reservedParams.includes(key) && value) {
        urlFilters[key] = value;
      }
    });
    return JSON.stringify(urlFilters);
  };

  const currentUrlFiltersKey = getCurrentUrlFilters();
  const areFiltersStale = items.length > 0 && previousFiltersRef.current !== currentUrlFiltersKey;

  // Close bulk actions dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (bulkActionsRef.current && !bulkActionsRef.current.contains(event.target as Node)) {
        setShowBulkActions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch items when the URL (page, filters) changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reservedParams = ['page', 'pageSize', 'sortBy', 'sortDesc', 'showAdvanced', 'view'];
    const urlFilters: Record<string, string> = {};

    params.forEach((value, key) => {
      if (!reservedParams.includes(key) && value) {
        urlFilters[key] = value;
      }
    });

    const currentFiltersKey = JSON.stringify(urlFilters);

    const urlPage = params.get('page');
    const urlPageSize = params.get('pageSize');
    const page = urlPage ? parseInt(urlPage, 10) : 1;
    const size = urlPageSize ? parseInt(urlPageSize, 10) : pageSize;

    // If URL has no filter params but searchFilters state has some, clear the state
    const hasUrlFilters = Object.keys(urlFilters).length > 0;
    const hasStateFilters = searchFilters && Object.keys(searchFilters).length > 0;
    if (!hasUrlFilters && hasStateFilters) {
      setSearchFilters({});
    }

    // Clear items immediately if filters changed (not just page number)
    if (previousFiltersRef.current !== currentFiltersKey) {
      setItems([]);
      previousFiltersRef.current = currentFiltersKey;
    }

    fetchItems(page, size, { ...urlFilters });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, { ...searchFilters });
  };

  const handleClearSearch = () => {
    clearSearchState();
    fetchItems(1, pageSize, {});
  };

  const handleReject = async (dto: CompetitionEntryDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error rejecting CompetitionEntry:', error);
    }
  };

  const handleConfirm = async (dto: CompetitionEntryDTO) => {
    try {
      await processEftPayment({ componentId: dto.id, entityType: 'Competition' });
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error confirming CompetitionEntry payment:', error);
    }
  };

  const handleSelectRow = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = items.map((item) => item.id).filter((id): id is string => !!id);
      setSelectedIds(new Set(allIds));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleBulkConfirm = async () => {
    const confirmed = await confirmAction({
      title: 'Confirm Entries',
      text: `Are you sure you want to confirm ${selectedIds.size} competition ${selectedIds.size > 1 ? 'entries' : 'entry'} as paid?`,
      icon: 'warning',
      confirmButtonText: 'Yes, Confirm',
      cancelButtonText: 'Cancel',
    });
    if (!confirmed) return;

    try {
      await Promise.all(
        Array.from(selectedIds).map((id) =>
          processEftPayment({ componentId: id, entityType: 'Competition' }),
        ),
      );
      setSelectedIds(new Set());
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error bulk confirming CompetitionEntries:', error);
    }
  };

  const handleBulkReject = async () => {
    const confirmed = await confirmAction({
      title: 'Reject Entries',
      text: `Are you sure you want to reject ${selectedIds.size} competition ${selectedIds.size > 1 ? 'entries' : 'entry'}? This will decline the entries without deleting them.`,
      icon: 'warning',
      confirmButtonText: 'Yes, Reject',
      cancelButtonText: 'Cancel',
    });
    if (!confirmed) return;

    try {
      await updateStatus(Array.from(selectedIds), CompetitionStatusConstants.DECLINED);
      setSelectedIds(new Set());
      await refreshAfterMutation();
    } catch (error: any) {
      console.error('Error bulk rejecting CompetitionEntries:', error);
      await showAlert({
        title: 'Unable to Reject Entries',
        text: error?.message || 'An error occurred while rejecting the selected competition entries. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/competitionentries"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission='Settings.CompetitionEntry.View'
    >
      <Card
        title="Manage Competition Entries"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <div className="flex items-center space-x-2">
            {roles?.includes('Admin') && selectedIds.size > 0 && (
              <div ref={bulkActionsRef} className="relative">
                <Button
                  variant="secondary"
                  onClick={() => setShowBulkActions(!showBulkActions)}
                  className="flex items-center gap-2"
                >
                  Confirm
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold rounded-full">
                    {selectedIds.size}
                  </span>
                  <ChevronDown
                    size={16}
                    className={`transition-transform duration-200 ${showBulkActions ? 'rotate-180' : ''
                      }`}
                  />
                </Button>
                {showBulkActions && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
                    <button
                      onClick={() => {
                        handleBulkConfirm();
                        setShowBulkActions(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-gray-100 dark:hover:bg-gray-700 first:rounded-t-lg"
                    >
                      Accept
                    </button>
                    <button
                      onClick={() => {
                        handleBulkReject();
                        setShowBulkActions(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700 last:rounded-b-lg"
                    >
                      Reject Selected
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        }
      >
        <div className="flex  justify-between items-center mb-4">
          {roles?.includes('Admin') && (
            <GeneralSearch
              searchFilters={searchFilters}
              setSearchFilters={setSearchFilters}
              onSubmit={handleSearch}
              onClear={handleClearSearch}
            />
          )}
        </div>

        <CompetitionEntryTable
          data={areFiltersStale ? [] : items}
          handleConfirm={handleConfirm}
          handleReject={handleReject}
          selectedIds={selectedIds}
          onSelectRow={handleSelectRow}
          onSelectAll={handleSelectAll}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          sortBy={sortBy}
          sortDescending={sortDescending}
          onSort={handleSort}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default CompetitionEntryList;